import {
  AlertTriangle,
  ArrowLeft,
  Clock,
  Package,
  RefreshCw,
  Truck,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { showToast } from "../../lib/toast";
import { productService } from "../../services/product.service";
import { purchaseReturnService } from "../../services/purchase-return.service";

interface ExpiryBatch {
  id: string;
  batchNumber: string;
  product: { id: string; name: string; sku?: string };
  supplier?: { id: string; name: string };
  expiryDate: string;
  quantity: number | string;
  mrp: number | string;
  purchasePrice?: number | string;
  costPrice?: number | string;
  stockValue?: number | string;
  daysUntilExpiry?: number;
  grnId?: string;
}

type ExpiryTab = "30" | "60" | "90" | "expired";

export default function ExpiryAlertDashboard() {
  const [activeTab, setActiveTab] = useState<ExpiryTab>("30");
  const [batches, setBatches] = useState<ExpiryBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatches, setSelectedBatches] = useState<Set<string>>(
    new Set(),
  );
  const [returningBatches, setReturningBatches] = useState(false);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      let data: ExpiryBatch[];
      if (activeTab === "expired") {
        data = await productService.getExpiredBatches();
      } else {
        data = await productService.getExpiringBatches(Number(activeTab));
      }
      setBatches(data);
      setSelectedBatches(new Set());
    } catch {
      showToast.error("Failed to load batches");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const toggleSelect = (id: string) => {
    setSelectedBatches((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedBatches.size === batches.length) {
      setSelectedBatches(new Set());
    } else {
      setSelectedBatches(new Set(batches.map((b) => b.id)));
    }
  };

  const handleReturnToSupplier = async () => {
    if (selectedBatches.size === 0) return;

    const selected = batches.filter((b) => selectedBatches.has(b.id));

    // Group by supplier + GRN
    const grouped = new Map<
      string,
      { grnId: string; items: typeof selected }
    >();
    for (const batch of selected) {
      if (!batch.grnId) continue;
      const key = batch.grnId;
      if (!grouped.has(key)) {
        grouped.set(key, { grnId: batch.grnId, items: [] });
      }
      grouped.get(key)!.items.push(batch);
    }

    if (grouped.size === 0) {
      showToast.error("Selected batches have no linked GRN for return");
      return;
    }

    setReturningBatches(true);
    try {
      for (const [, group] of grouped) {
        await purchaseReturnService.createPurchaseReturn(group.grnId, {
          returnReason: "EXPIRED",
          notes: `Auto-generated return for expired/expiring batches`,
          items: group.items.map((b) => ({
            productId: b.product.id,
            batchId: b.id,
            lotId: b.id,
            quantity: String(b.quantity),
            reason: `Batch ${b.batchNumber} ${activeTab === "expired" ? "expired" : "expiring"} on ${new Date(b.expiryDate).toLocaleDateString()}`,
          })),
        });
      }
      showToast.success(
        `Purchase return created for ${selectedBatches.size} batch(es)`,
      );
      loadBatches();
    } catch {
      showToast.error("Failed to create purchase return");
    } finally {
      setReturningBatches(false);
    }
  };

  const finiteNumber = (value: unknown, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const batchStockValue = (batch: ExpiryBatch) => {
    const canonicalValue = finiteNumber(batch.stockValue, Number.NaN);
    if (Number.isFinite(canonicalValue)) return canonicalValue;
    return (
      finiteNumber(batch.quantity) *
      finiteNumber(batch.purchasePrice ?? batch.costPrice ?? batch.mrp)
    );
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);

  const totalStockValue = batches.reduce(
    (sum, batch) => sum + batchStockValue(batch),
    0,
  );

  const tabs: { key: ExpiryTab; label: string; icon: React.ReactNode }[] = [
    {
      key: "30",
      label: "30 Days",
      icon: <AlertTriangle className="h-4 w-4" />,
    },
    { key: "60", label: "60 Days", icon: <Clock className="h-4 w-4" /> },
    { key: "90", label: "90 Days", icon: <Clock className="h-4 w-4" /> },
    {
      key: "expired",
      label: "Expired",
      icon: <XCircle className="h-4 w-4" />,
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Expiry Alert Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Monitor expiring and expired batches, initiate return-to-supplier
            workflow
          </p>
        </div>
        <button
          onClick={loadBatches}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Total Batches"
          value={batches.length.toString()}
          icon={<Package className="h-5 w-5 text-teal-600" />}
          bg="bg-teal-50"
        />
        <SummaryCard
          label="Total Stock Value at Risk"
          value={formatCurrency(totalStockValue)}
          icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
          bg="bg-red-50"
        />
        <SummaryCard
          label="Unique Products"
          value={new Set(batches.map((b) => b.product.id)).size.toString()}
          icon={<Package className="h-5 w-5 text-blue-600" />}
          bg="bg-blue-50"
        />
        <SummaryCard
          label="Selected for Return"
          value={selectedBatches.size.toString()}
          icon={<Truck className="h-5 w-5 text-purple-600" />}
          bg="bg-purple-50"
        />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Action Bar */}
      {selectedBatches.size > 0 && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <span className="text-sm font-medium text-amber-800">
            {selectedBatches.size} batch(es) selected
          </span>
          <button
            onClick={handleReturnToSupplier}
            disabled={returningBatches}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 disabled:opacity-50"
          >
            <ArrowLeft className="h-4 w-4" />
            {returningBatches ? "Creating Return..." : "Return to Supplier"}
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">Loading...</div>
        ) : batches.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No batches found</p>
            <p className="text-sm mt-1">
              {activeTab === "expired"
                ? "No expired batches in stock"
                : `No batches expiring within ${activeTab} days`}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="py-3 px-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedBatches.size === batches.length}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">
                    Product
                  </th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">
                    Batch No.
                  </th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">
                    Supplier
                  </th>
                  <th className="py-3 px-4 text-center font-medium text-gray-600">
                    Qty
                  </th>
                  <th className="py-3 px-4 text-right font-medium text-gray-600">
                    MRP
                  </th>
                  <th className="py-3 px-4 text-right font-medium text-gray-600">
                    Stock Value
                  </th>
                  <th className="py-3 px-4 text-center font-medium text-gray-600">
                    Expiry Date
                  </th>
                  <th className="py-3 px-4 text-center font-medium text-gray-600">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => {
                  const expDate = new Date(batch.expiryDate);
                  const now = new Date();
                  const daysLeft = Math.ceil(
                    (expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
                  );
                  const isExpired = daysLeft <= 0;
                  const isUrgent = daysLeft <= 30;

                  return (
                    <tr
                      key={batch.id}
                      className={`border-b border-gray-50 hover:bg-gray-50 ${
                        selectedBatches.has(batch.id) ? "bg-amber-50" : ""
                      }`}
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={selectedBatches.has(batch.id)}
                          onChange={() => toggleSelect(batch.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="py-3 px-4 font-medium text-gray-900">
                        {batch.product.name}
                        {batch.product.sku && (
                          <span className="text-xs text-gray-400 ml-1">
                            ({batch.product.sku})
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 font-mono text-xs">
                        {batch.batchNumber}
                      </td>
                      <td className="py-3 px-4 text-gray-600">
                        {batch.supplier?.name ?? "—"}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {batch.quantity}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {formatCurrency(finiteNumber(batch.mrp))}
                      </td>
                      <td className="py-3 px-4 text-right font-medium">
                        {formatCurrency(batchStockValue(batch))}
                      </td>
                      <td className="py-3 px-4 text-center text-xs">
                        {expDate.toLocaleDateString("en-IN")}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${
                            isExpired
                              ? "bg-red-100 text-red-700"
                              : isUrgent
                                ? "bg-amber-100 text-amber-700"
                                : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {isExpired ? "Expired" : `${daysLeft}d left`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon,
  bg,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  bg: string;
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            {label}
          </p>
          <p className="mt-1 text-xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`${bg} p-3 rounded-xl`}>{icon}</div>
      </div>
    </div>
  );
}
